import { FrontEndModel } from '@/models/FrontEndModel';
import { AthleteProfileModel, BAPurchaseModel, BARatingFormula } from '@/models';
import { EventLocationModel } from './EventLocationModel';
import { getTime, formatDate, formatDateAndTime } from '@/helpers';
import { baEventApi, eventLocationApi } from '@/api/BAEventsApi';
import { athleteApi } from '@/api/AthleteApi';
import { isEmpty, isNotEmpty, isZero, isEmptyArray, snipUUID, fullName, weightStr, heightStringToInches, heightStr } from '@/pipes';
import { AgreementFile } from '@/models/file/FileModel';
import { ObjectCache } from '@/helpers/object-cache';
import { BARatingProfileModel } from '../athlete/BARatingProfileModel';
import { baRatingFormulaApi } from '@/api/BARatingFormulaApi';
import { CustomerCountry, Length, Mass, SportName } from '@/../types';
import { userStore } from '@/store';
import { RecruitingProfileModel } from '../athlete/RecruitingProfileModel';
import { Country, WebLink } from '@best-athletes/ba-types';

export enum BAEventStatus {
    draft = "Draft",
    scheduled = "Scheduled",
    inprogress = "In Progress",
    complete = "Complete",
    deleted = "Deleted",
    archived = "Archived",
}
export enum CouponDiscountType {
    fixed = "Fixed",
    percent = "Percent",
    show = "Show",
}
export class EventTicketCoupon {
    name!: string;
    code!: string;
    discountType: CouponDiscountType = CouponDiscountType.show;
    discountAmount!: number;

    load(obj: Record<string, any>): this {
        Object.assign(this, obj);
        return this;
    }
}
export class EventTicketModel extends FrontEndModel {
    name!: string;
    description?: string;
    price!: number;
    priceUS?: number;
    capacity: number = -1;
    remaining: number = -1;
    stripeId?: string;
    stripeIdUS?: string;
    deleted: boolean = false;
    hidden: boolean = false;
    disabled: boolean = false;
    coupons: Array<EventTicketCoupon>;
    currencyCountry: CustomerCountry;

    constructor(id?: string) {
        super();
        this.initializeFromId(id);
    }
    setCountry(country: string) {
        if( isEmpty(country) ) country = userStore.CurrentUserCountry;

		if( (country.toLowerCase().startsWith('ca')) ) this.currencyCountry = CustomerCountry.CA;
        else if( (country.toLowerCase().startsWith('us')) ) this.currencyCountry = CustomerCountry.US;
        else if( (country.toLowerCase().includes('united states')) ) this.currencyCountry = CustomerCountry.US;

        if( isEmpty(this.currencyCountry) ) this.currencyCountry = userStore.CurrentUserCountry;
        if( isEmpty(this.currencyCountry) ) this.currencyCountry = CustomerCountry.US;
    }
    get CurrencyCountry(): CustomerCountry {
        if( isEmpty(this.currencyCountry) ) this.setCountry(userStore.CurrentUserCountry);
        return this.currencyCountry;
    }
    get ButtonAction(): string {
        if( this.name.toLowerCase().includes('regist') ) return 'Register';
        if( this.IsFree ) return 'Acquire';
        return 'Purchase';
    }
    ButtonActionLong(title: string = 'Event', ticketName: string = ''): string {
        if( this.name.toLowerCase().includes('regist') ) return `Register for ${title}`;
        if( this.IsFree ) return `Acquire ticket for ${title}`;
        return `Purchase ${ticketName} ticket for ${title}`;
    }
    get ButtonTooltip(): string {
        if( this.name.toLowerCase().includes('regist') ) return this.name;
        if( this.IsFree ) return `Acquire ${this.name}`;
        return `Purchase ${this.name}`
    }
    get ButtonTooltipLong(): string {
        return this.description;
    }
    async initializeFromId(id?: string){
        this.id = id?? undefined;
        if( isNotEmpty(id) ) {
            this.SetBlank();
            if( id === "GA" ) this.SetGeneralAdmission();
            if( id === "VIP" ) this.SetVIP();
            if( id === "new" ) {
                const {uuid} = await baEventApi.uuid();
                this.id = uuid;
            }
            this.setRemaining();
        }
    }
    load(obj: Record<string, any>): this {
		Object.assign(this, obj);
        return this;
    }

    assign(ticketType: EventTicketModel) {
        Object.assign(this, ticketType);
        this.id = ticketType.id;
        this.setRemaining();
    }
    setRemaining() {
        if( this.capacity < 0 || this.remaining < 0 || this.remaining > this.capacity ) this.remaining = this.capacity;
    }
    get Description(): string {
        if( isEmpty(this.description) ) return '';
        return this.description;
    }
    get DescriptionTitle(): string {
        if( isEmpty(this.description) ) return '';
        return this.description.split(/\r?\n/)[0];
    }
    
    get Capacity(): string {
        if( this.deleted ) return 'deleted';
        if( this.capacity < 0 ) return 'unlimited';
        return this.capacity.toString();
    }
    get Remaining(): string {
        if( this.deleted ) return '0';
        if( this.capacity < 0 ) return 'yes';
        return this.remaining.toString();
    }
    get IsFree(): boolean {
        if( this.CurrencyCountry === CustomerCountry.CA ) return isZero(this.price);;
        return isZero(this.priceUS);
    }
    get Price(): string {
        if( this.IsFree ) return 'Free';
        if( this.CurrencyCountry === CustomerCountry.CA ) return `CAD ${this.PriceCAD}`;
        return `US ${this.PriceUS}`;
    }
    get PriceCAD(): string {
        if( this.IsFree ) return 'Free';
        return `$${Number(this.price).toFixed(0)}`
    }
    get PriceUS(): string {
        if( isZero(this.priceUS) ) return 'Free';
        return `$${Number(this.priceUS).toFixed(0)}`
    }
    computeProcessingFee(addFee: boolean, addStripeFee: boolean = true, addBAFee: boolean = true, baPercent: number = 0.05): number {
        if( this.IsFree ) return 0;
        if( !addFee ) return 0;

        const price: number = (this.CurrencyCountry === CustomerCountry.CA)? this.price : this.priceUS;
        // stripe fees
        // standard:    0.029 + 0.30
        // tax:         0.01
        // conversion:  0.02
        const stripeFee: number = addStripeFee? price * 0.029 + 0.30 : 0;
        const stripeTaxFee: number = addStripeFee? price * 0.01 : 0;
        const stripeConversionFee: number = addStripeFee? price * 0.02 : 0;
        // best athletes fees
        // standard:    0.05
        const baFee: number = addBAFee? price * baPercent : 0;
        let totalFees: number = stripeFee + stripeTaxFee + baFee;
        if( totalFees > 100 ) totalFees = 100;
        return totalFees;
    }
    get ToolTip(): string {
        return isEmpty(this.description)? this.name : this.description;
    }
    get HasCoupons(): boolean {
        return isNotEmpty(this.coupons);
    }

    // set ticket to special type
    private SetBlank() {
        this.id = undefined;
        this.name = '';
        this.description = '';
        this.price = 395;
        this.priceUS = 395;
        this.capacity = -1;
        this.remaining = -1;
        this.deleted = false;
    }
    private SetGeneralAdmission() {
        this.id = undefined;
        this.name = 'General Admission';
        this.description = 'Valid for general entry';
        this.price = 395;
        this.priceUS = 395;
        this.capacity = -1;
    }
    private SetVIP() {
        this.id = undefined;
        this.name = 'VIP';
        this.description = 'Special ticket';
        this.price = 995;
        this.priceUS = 995;
        this.capacity = 5;
    }
}

export class EventParticipantModel extends FrontEndModel {
    attendeeId!: string;
    ticketId!: string;
    purchaseId?: string;
    redeemed: boolean = false;
    tags: string;
}

export class EventParticipantDetails extends EventParticipantModel {
    athlete: AthleteProfileModel;
    ticket: EventTicketModel;
    purchase: BAPurchaseModel;
    baRating: BARatingProfileModel;
    baRatingFormula: BARatingFormula;
    recruitingProfile: RecruitingProfileModel;
    data: Record<string, any> = {};

    async fetchBARating(): Promise<BARatingProfileModel> {
        if( !this.athlete ) return undefined;
        this.baRatingFormula = await baRatingFormulaApi.findActive();
        this.baRating = await athleteApi.getBARating(this.athlete.id);
        return this.baRating;
    }
    get FullName(): string {
        if( isNotEmpty(this.athlete) ) return this.athlete.FullName;
        const data = this.Data();
        return fullName(data?.firstName, data?.lastName);
    }
    get Email(): string {
        if( isNotEmpty(this.athlete ) ) return this.athlete.email;
        return this.data?.email;
    }
    get TeamGender(): string {
        if( this.athlete?.HasTeam ) return this.athlete.team.gender;
        else if( this.recruitingProfile?.HasTeam ) return this.recruitingProfile.team.gender;
        else if( this.baRating?.HasTeam ) return this.baRating.team.gender;
        return undefined;
    }
    get TeamName(): string {
        if( this.athlete?.HasTeam ) return this.athlete.team.name;
        else if( this.recruitingProfile?.HasTeam ) return this.recruitingProfile.team.name;
        else if( this.baRating?.HasTeam ) return this.baRating.team.name;
        return undefined;
    }
    get Organization(): string {
        if( this.athlete?.fromOrganization ) return this.athlete.fromOrganization;
        if( this.recruitingProfile?.fromOrganization ) return this.recruitingProfile.fromOrganization;
        if( this.baRating?.fromOrganization ) return this.baRating.fromOrganization;
        if( !!this.athlete?.data ) {
            const keys = Object.keys(this.athlete.data);
            const key = keys.find(k => k.toLowerCase().includes('club'));
            if( isNotEmpty(key) ) return this.athlete.data[key];
        }
        return this.TeamName;
    }
    get DateOfBirth(): string {
        if( isNotEmpty(this.athlete?.birthDate) ) return this.athlete.BirthDate;
        if( isNotEmpty(this.recruitingProfile?.birthDate) ) return this.recruitingProfile.BirthDate;
        if( isNotEmpty(this.baRating?.birthDate) ) return this.baRating.BirthDate;
        if( !!this.athlete?.data ) {
            const keys = Object.keys(this.athlete.data);
            const key = keys.find(k => k.toLowerCase().includes('birth'));
            if( isNotEmpty(key) ) return this.athlete.data[key];
        }
        return '';
    }
    get Weight(): string {
        if( isNotEmpty(this.athlete?.weight) ) return this.athlete.WeightStr;
        if( isNotEmpty(this.recruitingProfile?.weight) ) return this.recruitingProfile.WeightStr;
        if( isNotEmpty(this.baRating?.weight) ) return this.baRating.WeightStr;
        const weightInLbs = this.FromData('weight');
        return weightStr(Number(weightInLbs), Mass.POUND, Mass.POUND);
    }
    get Height(): string {
        if( isNotEmpty(this.athlete?.height) ) return this.athlete.HeightStr;
        if( isNotEmpty(this.recruitingProfile?.height) ) return this.recruitingProfile.HeightStr;
        if( isNotEmpty(this.baRating?.height) ) return this.baRating.HeightStr;
        const heightInIn = this.FromData('height');
        return heightStr(heightStringToInches(heightInIn), Length.INCH, Length.INCH);
    }
    get Position(): string {
        return this.athlete?.PositionShort;
    }
    get PictureUrl(): string {
        return this.athlete?.pictureUrl;
    }
    get Location(): string {
        return this.athlete?.Location;
    }
    get Country(): string {
        let countryStr: string = undefined;
        if( isNotEmpty(this.athlete?.country) ) countryStr = this.athlete.Country;
        else if( isNotEmpty(this.recruitingProfile?.country) ) countryStr = this.recruitingProfile.Country;
        else if( isNotEmpty(this.baRating?.country) ) countryStr = this.baRating.Country;
        else countryStr = this.FromData('country');

        if( countryStr.toUpperCase().trim().includes("USA") ) countryStr = Country.US;
        return countryStr;
    }
    FromSpecificData(label: string, data: Record<string, any>): string {
        if( !data || data.length === 0 ) return undefined;
        const keys = Object.keys(data);
        const key = keys.find(k => k.toLowerCase().includes(label));
        return data[key];
    }
    FromData(label: string): string {
        let result: string = this.FromSpecificData(label, this.athlete?.data);
        if( isEmpty(result) ) result = this.FromSpecificData(label, this.data);
        return result;
    }
    get Ticket(): string {
        if( !this.ticket ) return snipUUID(this.ticketId, 5);
        return this.ticket.name.substring(0,3) + ': ' + this.ticket.Price;
    }
    Data(moreLabels: Array<string> = [], moreData: Record<string, any> = undefined): Record<string, any> {
        const normalizeKey = (key: string): string => {
            return key.toLowerCase().replace(/[^a-z0-9]/g, "");
        };

        const athleteKeys = new Set<string>();
        if( isNotEmpty(this.athlete) ) Object.keys(this.athlete).forEach(key => athleteKeys.add(key));
        else if( isNotEmpty(this.recruitingProfile?.athleteProfile) ) Object.keys(this.recruitingProfile.athleteProfile).forEach(key => athleteKeys.add(key));
        else if( isNotEmpty(this.baRating?.athleteProfile) ) Object.keys(this.baRating.athleteProfile).forEach(key => athleteKeys.add(key));
        const athleteLabels = Array.from(athleteKeys);

        const dataLabels = new Set<string>();
        if( isNotEmpty(moreLabels) ) moreLabels.forEach(l => dataLabels.add(l));
        if( isNotEmpty(this.data) ) Object.keys(this.data).forEach(key => dataLabels.add(key));
        if( isNotEmpty(this.athlete?.data) ) Object.keys(this.athlete.data).forEach(key => dataLabels.add(key));
        if( isNotEmpty(this.recruitingProfile?.data) ) Object.keys(this.recruitingProfile.data).forEach(key => dataLabels.add(key));
        if( isNotEmpty(this.recruitingProfile?.athleteProfile?.data) ) Object.keys(this.recruitingProfile.athleteProfile.data).forEach(key => dataLabels.add(key));
        if( isNotEmpty(this.baRating?.data) ) Object.keys(this.baRating.data).forEach(key => dataLabels.add(key));
        if( isNotEmpty(this.baRating?.athleteProfile?.data) ) Object.keys(this.baRating.athleteProfile.data).forEach(key => dataLabels.add(key));

        const participantData: Record<string, any> = {};
        dataLabels.forEach(l => {
            const participantKeys = Object.keys(participantData);
            const participantKey = participantKeys.find(k => normalizeKey(k) === normalizeKey(l));
            if( isEmpty(participantKey) ) {
                const athleteLabel = athleteLabels.find(athleteKey => normalizeKey(athleteKey) === normalizeKey(l));
                if( isEmpty(participantData[l]) && normalizeKey(l).includes('birth') ) participantData[l] = this.DateOfBirth;
                if( isEmpty(participantData[l]) && normalizeKey(l).includes('org') ) participantData[l] = this.Organization;
                if( isEmpty(participantData[l]) && normalizeKey(l).includes('team') ) participantData[l] = this.TeamName;
                if( isEmpty(participantData[l]) && normalizeKey(l).includes('weight') ) participantData[l] = this.Weight;
                if( isEmpty(participantData[l]) && normalizeKey(l).includes('height') ) participantData[l] = this.Height;
                if( isEmpty(participantData[l]) && normalizeKey(l).includes('country') ) participantData[l] = this.Country;

                if( normalizeKey(l).includes('teamgender') ) {
                    const teamGender: string = this.TeamGender;
                    participantData[l] = (teamGender && (teamGender.toLowerCase().includes('coed')))? undefined : teamGender;
                }

                if( isEmpty(participantData[l]) && isNotEmpty(moreData) && isNotEmpty(moreData[l]) ) participantData[l] = moreData[l];
                if( isEmpty(participantData[l]) && isNotEmpty(athleteLabel) ) {
                    if( isNotEmpty(this.athlete) && isNotEmpty(this.athlete[athleteLabel]) ) participantData[l] = this.athlete[athleteLabel];
                    else if( isNotEmpty(this.recruitingProfile?.athleteProfile) && isNotEmpty(this.recruitingProfile.athleteProfile[athleteLabel]) ) participantData[l] = this.recruitingProfile.athleteProfile[athleteLabel];
                    else if( isNotEmpty(this.baRating?.athleteProfile) && isNotEmpty(this.baRating.athleteProfile[athleteLabel]) ) participantData[l] = this.baRating.athleteProfile[athleteLabel];
                }
                if( isEmpty(participantData[l]) && isNotEmpty(this.data) && isNotEmpty(this.data[l]) ) participantData[l] = this.data[l];
                if( isEmpty(participantData[l]) && isNotEmpty(this.athlete?.data) && isNotEmpty(this.athlete.data[l]) ) participantData[l] = this.athlete.data[l];
                if( isEmpty(participantData[l]) && isNotEmpty(this.recruitingProfile?.data) && isNotEmpty(this.recruitingProfile.data[l]) ) participantData[l] = this.recruitingProfile.data[l];
                if( isEmpty(participantData[l]) && isNotEmpty(this.recruitingProfile?.athleteProfile?.data) && isNotEmpty(this.recruitingProfile.athleteProfile.data[l]) ) participantData[l] = this.recruitingProfile.athleteProfile.data[l];
                if( isEmpty(participantData[l]) && isNotEmpty(this.baRating?.data) && isNotEmpty(this.baRating.data[l]) ) participantData[l] = this.baRating.data[l];
                if( isEmpty(participantData[l]) && isNotEmpty(this.baRating?.athleteProfile?.data) && isNotEmpty(this.baRating.athleteProfile.data[l]) ) participantData[l] = this.baRating.athleteProfile.data[l];
                if( isEmpty(participantData[l]) ) participantData[l] = ' - ';
            }
        })

        return participantData;
    }
}

export class BAEventModel extends FrontEndModel {
    name!: string;
    menu!: string;
    archived: boolean = false;
    description!: string;
    description2?: string;
    date: Date = new Date();
    endDate: Date = new Date();
    multiDay: boolean = false;
    location: string = '';                              // location ID
    photo: string = '';
    fileUrl: string = '';
    capacity: number = -1;
    status?: BAEventStatus;
    sponsors: Array<string> = [];                       // sponsor IDs
    participants: Array<EventParticipantModel> = [];    // participants with tickets
    coaches: Array<string> = [];                        // coaches invited to view event
    tickets: Array<EventTicketModel> = [];              // ticket types
    stripeId?: string;
    url: string = '';
    meetingUrl?: string;
    showcase: boolean = false;
    showcaseStart: Date;
    showcaseEnd: Date;
    allday: boolean = false;                            // all-day event does not have a start time
    invitational: boolean = false;                      // invite-only event
    registration: boolean = false;
    addProcessingFee: boolean = true;                   // include the processing fee in addition to ticket cost
    processingFeeStripe: boolean = true;                // the processing fee for stripe charges
    processingFeeBA: boolean = false;                   // the processing fee charged by Best Athletes
    processingFeeBApercent: number = 0.05;              // Best Athlete processing fee (default 5%)
    prerequisite?: string;                              // registration that must be completed before this event
    sportName?: SportName;
    dataLabels: Array<string> = ['First Name', 'Last Name', 'Email', 'Comments'];
    agreements: Array<AgreementFile> = [];
    manualCheckin: boolean = true;
    notes: string = '';
    weblinks?: Array<WebLink>;

    // ex fields
    locationDetails: EventLocationModel = undefined;

	load(obj: Record<string, any>): this {
		Object.assign(this, obj);
		if( obj['date'] ) {
            this.date = new Date( obj['date'] );
        } else {
            this.date = new Date();
        }
        if( obj['endDate'] ) {
            this.endDate = new Date( obj['endDate'] );
        } else {
            this.endDate = new Date();
        }
        if( obj['tickets'] ) {
            this.tickets = [];
            for( const ticketType of obj['tickets'] ) {
                var ticketObj = new EventTicketModel(ticketType.id);
                ticketObj.assign(ticketType);
                this.tickets.push(ticketObj);
            }
        }
        if( obj['showcaseStart'] ) {
            this.showcaseStart = new Date( obj['showcaseStart'] );
        } else {
            this.showcaseStart = new Date();
            this.showcaseStart.setDate(this.date.getDate() - 14);
        }
        if( obj['showcaseEnd'] ) {
            this.showcaseEnd = new Date( obj['showcaseEnd'] );
        } else {
            this.showcaseEnd = new Date();
            this.showcaseEnd.setDate(this.date.getDate() + 5);
        }
        if( obj['agreements']) {
            this.agreements = [];
            for( const agreement of obj['agreements'] ) {
                var agreementObj = new AgreementFile(agreement.title, agreement.fileUrl);
                this.agreements.push(agreementObj);
            }
        }
        
		return this;
	}
    async loadEx(locationsCache: ObjectCache<EventLocationModel> = undefined) {
        await this.getLocation(locationsCache);
        if( isNotEmpty(this.locationDetails) ) this.tickets.forEach(t => t.setCountry(this.locationDetails?.Country))
    }

    get Menu(): string {
        if( isNotEmpty(this.menu) ) return this.menu;
        return this.name;
    }
    // locations: ObjectCache<EventLocationModel> = new ObjectCache<EventLocationModel>();
    get HasLocation(): boolean {
        return( isNotEmpty(this.location) );
    }
    async getLocation(locationsCache: ObjectCache<EventLocationModel>) : Promise<EventLocationModel> {
        if( !this.HasLocation ) return undefined;

        if( isNotEmpty(locationsCache) ) {
            this.locationDetails = locationsCache.get(this.location);
            if( isNotEmpty(this.locationDetails) ) return this.locationDetails;
        }

        this.locationDetails = await eventLocationApi.findById(this.location);

        if( isNotEmpty(locationsCache) ) {
            locationsCache.set(this.locationDetails);
        }
        return this.locationDetails;
    }
    get Online(): boolean {
        if( !this.Location ) return false;
        return this.Location.online;
    }
    get Location(): EventLocationModel {
        return this.locationDetails;
    }
    get LocationName(): string {
        if( !this.Location ) return '';
        return this.locationDetails.name;
    }
    get LocationAddress(): string {
        if( !this.Location ) return '';
        return this.locationDetails.address;
    }
    get Locale(): string {
        if( !this.Location ) return '';
        return this.locationDetails.Location;
    }
    get LocationMap(): string {
		if( !this.Location ) return `https://maps.google.com/`;
        return this.locationDetails.GoogleMap();
    }
    get HasWebsite(): boolean {
        return( !!this.url && this.url.length > 0 );
    }
    get Website(): string {
        if( !this.HasWebsite ) return '';
        return this.url;
    }
    get IsToday(): boolean {
        if( !this.date ) return false;
        const today = new Date();
    
        return (
            this.date.getFullYear() === today.getFullYear() &&
            this.date.getMonth() === today.getMonth() &&
            this.date.getDate() === today.getDate()
        );
    }
    get IsPast(): boolean {
        if( !this.date ) return false;
		const today = new Date();
		const yesterday = new Date;
		yesterday.setDate(today.getDate() - 1);
		return this.date < yesterday;
    }
    get IsFuture(): boolean {
        if( !this.date ) return false;
		const today = new Date();
		const twoDaysAgo = new Date;
		twoDaysAgo.setDate(today.getDate() - 2);
		return this.date >= twoDaysAgo;
    }
    get DateStr(): string {
        if( !this.date ) return new Date().toDateString();
        return this.date.toDateString();
    }
    get EndDateStr(): string {
        if( !this.endDate ) return this.DateStr;
        return this.endDate.toDateString();
    }
    get TimeStr(): string {
        if( !this.date ) return '';
        return getTime(this.date);
    }
    get EndTimeStr(): string {
        if( !this.endDate ) return this.TimeStr;
        return getTime(this.endDate);
    }
    get DateFormatted(): string {
        if( !this.date ) return '';
        return formatDate(this.date);
    }
    get ShowcaseStartDateAndTime(): string {
        if( !this.showcaseStart ) return '';
        return formatDateAndTime(this.showcaseStart);
    }
    get ShowcaseEndDateAndTime(): string {
        if( !this.showcaseEnd ) return '';
        return formatDateAndTime(this.showcaseEnd);
    }

    get Capacity(): string {
        if( this.capacity < 0 ) return 'unlimited';
        return this.capacity.toString();
    }

    get Tickets(): EventTicketModel[] {
        return this.tickets.filter(t => !t.deleted );
    }
    get AvailableTickets(): Array<EventTicketModel> {
        return this.Tickets.filter(t => !t.hidden );
    }
    FilteredTickets(couponCode: string = '') : Array<EventTicketModel> {
        if( isEmpty(couponCode) ) return this.AvailableTickets.filter(t => t.coupons.findIndex(c => c.discountType === CouponDiscountType.show) < 0);
        return this.AvailableTickets.filter(t => t.coupons.findIndex(c => c.discountType === CouponDiscountType.show && couponCode.includes(c.code)) >= 0 );
    }

    Ticket(ticketId: string): EventTicketModel {
        if( !this.HasTickets ) return undefined;
        return this.tickets.find(ticket => ticket.id == ticketId);
    }
    OneTicket(couponCode: string): EventTicketModel {
        if( !this.HasOneTicket(couponCode) ) return undefined;
        return this.FilteredTickets(couponCode)[0];
    }
    get HasTickets(): boolean {
        if( !this.tickets ) return false;
        return ( this.tickets.length > 0 );
    }
    get HasTicketsWithCode(): boolean {
        if( !this.HasTickets ) return false;
        return this.AvailableTickets.some(t => t.HasCoupons);
    }
    HasOneTicket(couponCode: string): boolean {
        if( isEmpty(this.FilteredTickets(couponCode)) ) return false;
        return ( this.FilteredTickets(couponCode).length === 1 );
    }

    ProcessingFee(ticket: EventTicketModel): number {
        return ticket.computeProcessingFee(this.addProcessingFee, this.processingFeeStripe, this.processingFeeBA, this.processingFeeBApercent);
    }
    ProcessingFeeStr(ticket: EventTicketModel): string {
        if( isEmpty(ticket) ) return '';
        return `Processing fee: $${this.ProcessingFee(ticket).toFixed(2)}`;
    }
    get ProcessingFeeTooltip(): string {
        return `This is a fee charged by ${this.processingFeeStripe? 'Stripe ':''}${(this.processingFeeBA && this.processingFeeStripe)? 'and ':''}${this.processingFeeBA? 'Best Athletes ':''}to power the event`
    }

    get HasParticipants(): boolean {
        if( isEmptyArray(this.participants) ) return false;
        return ( this.participants.length > 0 );
    }

    get Groups(): Array<EventTicketModel> {
        return this.Tickets.map(t => { 
            const group = new EventTicketModel;
            group.load(t);
            group.disabled = false;
            return group;
        })
    }

    AthleteTicket(athleteId: string): EventTicketModel {
        const attendee = this.participants.find(a => a.attendeeId === athleteId)
        if( !attendee ) return undefined;

        return this.tickets.find(t => t.id == attendee.ticketId);
    }

    isAttending(attendeeId: string): boolean {
        if( !this.HasParticipants ) return false;

        return this.participants.some(a => a.attendeeId === attendeeId);
    }
    async addAttendee(athlete: AthleteProfileModel, ticket: EventTicketModel): Promise<boolean> {
        // if athlete is already attending, do not add again
        if( this.isAttending(athlete.id) ) return false;

        // add attendee to event
        const attendee = new EventParticipantModel;
        attendee.attendeeId = athlete.id;
        attendee.ticketId = ticket.id;
        this.participants.push(attendee);

        // add event to attendee
        var athlete: AthleteProfileModel = await athleteApi.findById(attendee.attendeeId);
        if( !!athlete && athlete.addEvent(this) ) {
            await athleteApi.save(athlete)
        }
        return true;
    }
    async deleteAttendee(attendeeDetails: EventParticipantModel): Promise<boolean> {
        var index = -1;
        if( attendeeDetails.attendeeId && attendeeDetails.ticketId ) {
            index = this.participants.findIndex(item => ( item.attendeeId == attendeeDetails.attendeeId ) && ( item.ticketId == attendeeDetails.ticketId ));
        } else if( attendeeDetails.attendeeId ) {
            index = this.participants.findIndex(item => ( item.attendeeId == attendeeDetails.attendeeId ) && ( !attendeeDetails.ticketId ));
        } else if( attendeeDetails.ticketId ) {
            index = this.participants.findIndex(item => ( !attendeeDetails.attendeeId ) && ( item.ticketId == attendeeDetails.ticketId ));
        }
        if( index < 0 ) return false;

        // remove attendee from event
        this.participants.splice(index, 1);

        // remove event from attendee
        try{
            var athlete: AthleteProfileModel = await athleteApi.findById(attendeeDetails.attendeeId);
            if( !!athlete && athlete.removeEvent(this.id) ) {
                await athleteApi.save(athlete);
            }
        } catch(e) {
            console.error(`Athlete not found: ${attendeeDetails?.attendeeId}: `, attendeeDetails);
        }
        return true;
    }
    async deleteAllParticipants(): Promise<boolean> {
        while( this.HasParticipants ) {
            const last = this.participants[this.participants.length - 1];
            if( !this.deleteAttendee(last) ) return false;
        }
        return true;
    }

    HasFile(): boolean {
        return isNotEmpty(this.fileUrl);
    }

    get ShareLink(): string {
        return `baevent/${this.id}`;
    }
}